import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { Control, Controller, UseFormSetValue } from 'react-hook-form'
import { InferType } from 'yup'
import { MultiValue } from 'react-select'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { AsyncSelectDropdown } from '../../common/SelectDropdown'
import { Button } from '../../common/Button'
import { IconChoices } from '../../common/Icon'
import { chatMessageSchema } from '../../../forms/schemas/chat_message'
import { DropdownSelect } from '../../common/DropdownSelect'
import { TextInput } from '../../common/TextInput'
import { Avatar } from '../../common/Avatar'
import { DropdownItemContent } from '../../feed/DropdownItemContent'
import { useUserBlockedInSociety } from '../../../hooks/useUserBlockedInSociety'
import { BlockedCallout } from '../../blocked/BlockedCallout'
import { societyQueries } from '../../../api/societies'
import { useAuth } from '../../../hooks/useAuth'
import { useSelectedChatId } from '../../../state/context/SelectedChatIdProvider'
import { RecipientRow } from '../../../generated/api/chat-recipients'
import { GET_RECIPIENTS_LIMIT, chatQueries } from '../../../api/chat'
import {
  RecipientOption,
  formatRecipientOptionLabel,
  toHintOption,
  toRecipientOption,
} from './recipientOption'

const SEARCH_DEBOUNCE_MS = 300

interface StartNewChatHeaderProps {
  control: Control<InferType<typeof chatMessageSchema>>
  watchRecipients: RecipientRow[] | undefined
  watchIsGroupchat: boolean
  watchSociety: string | undefined
  setValue: UseFormSetValue<InferType<typeof chatMessageSchema>>
}

export const StartNewChatHeader = ({
  control,
  watchRecipients,
  watchIsGroupchat,
  watchSociety,
  setValue,
}: StartNewChatHeaderProps): ReactNode => {
  const { translate } = useAppTranslation()
  const queryClient = useQueryClient()

  const userIsBlockedInSociety = useUserBlockedInSociety(watchSociety)
  const { userId: authUserId } = useAuth()

  const { data: userSocieties } = useQuery(
    societyQueries.getUserSocieties(authUserId)
  )
  const societiesWithChatInterestsEnabled = userSocieties?.filter(
    (society) => !society.settings?.chatInterestsDisabled
  )

  const noGroupchatSocieties =
    societiesWithChatInterestsEnabled &&
    societiesWithChatInterestsEnabled.length === 0

  const societyDropdownOptions = societiesWithChatInterestsEnabled?.map(
    (society) => ({
      value: society._id,
      label: society.name,
      image: <Avatar size={30} url={society.coverPhoto?.url} />,
    })
  )

  const privateDropdownOptions = [
    {
      label: translate('startNewChat.openGroupChat'),
      value: 'false',
      icon: IconChoices.OPEN_EYE,
    },
    {
      label: translate('startNewChat.closedGroupChat'),
      value: 'true',
      icon: IconChoices.CLOSED_EYE,
    },
  ]

  const { setSelectedChatRoomId } = useSelectedChatId()

  useEffect(() => {
    if (watchIsGroupchat && noGroupchatSocieties) {
      setValue('isGroupchat', false)
    }
  }, [noGroupchatSocieties, setValue, watchIsGroupchat])

  const recipientsQuery = useMemo(
    () =>
      watchIsGroupchat
        ? { type: 'interests' as const, societyId: watchSociety }
        : { type: 'oneonone' as const, societyId: undefined },
    [watchIsGroupchat, watchSociety]
  )

  const fetchRecipients = useCallback(
    async (search: string): Promise<RecipientOption[]> => {
      if (recipientsQuery.type === 'interests' && !recipientsQuery.societyId) {
        return []
      }
      const page = await queryClient.fetchQuery(
        chatQueries.getRecipients({
          type: recipientsQuery.type,
          societyId: recipientsQuery.societyId,
          search: search.trim() || undefined,
          page: 1,
          limit: GET_RECIPIENTS_LIMIT,
        })
      )
      const options = page.items.map(toRecipientOption)
      return page.pages > 1
        ? options.concat(
            toHintOption(
              translate('chatRoomsSidebar.createNewMessageForm.search.moreHint')
            )
          )
        : options
    },
    [queryClient, recipientsQuery, translate]
  )

  const debounceTimer = useRef<ReturnType<typeof setTimeout>>()
  const pendingResolve = useRef<
    ((options: RecipientOption[]) => void) | undefined
  >()

  const loadOptions = useCallback(
    (search: string): Promise<RecipientOption[]> => {
      clearTimeout(debounceTimer.current)
      pendingResolve.current?.([])
      return new Promise<RecipientOption[]>((resolve) => {
        pendingResolve.current = resolve
        debounceTimer.current = setTimeout(() => {
          pendingResolve.current = undefined
          fetchRecipients(search).then(resolve, () => resolve([]))
        }, SEARCH_DEBOUNCE_MS)
      })
    },
    [fetchRecipients]
  )

  const groupChatSocietyFor = (recipient: RecipientRow): string | undefined => {
    const enabledInRecipientSociety = societiesWithChatInterestsEnabled?.some(
      (society) => society._id === recipient.societyId
    )
    return enabledInRecipientSociety
      ? recipient.societyId
      : societiesWithChatInterestsEnabled?.[0]?._id
  }

  const onCustomChange = (recipients: RecipientRow[]): void => {
    if (recipients.length === 0) {
      setSelectedChatRoomId(undefined)
      setValue('recipients', [], { shouldValidate: true })
      return
    }
    if (recipients.length === 1 && !watchIsGroupchat) {
      setSelectedChatRoomId(recipients[0].roomId)
    }
    if (recipients.length > 1) {
      if (noGroupchatSocieties) {
        const newRecipient = recipients[recipients.length - 1]
        setValue('recipients', [newRecipient], { shouldValidate: true })
        setSelectedChatRoomId(newRecipient.roomId)
        return
      }
      setValue('isGroupchat', true)
      setSelectedChatRoomId(undefined)
      const societyId = groupChatSocietyFor(recipients[0])
      societyId && setValue('society', societyId)
    }
    setValue('recipients', recipients, { shouldValidate: true })
  }

  const onSocietyDropdownChange = (
    societyId: string,
    fieldOnChange: (value: string) => void
  ): void => {
    if (societyId === watchSociety) {
      return
    }
    fieldOnChange(societyId)
    setValue('recipients', [], { shouldValidate: true })
  }

  const handleChangeToGroupChat = (): void => {
    setValue('isGroupchat', true)
    setSelectedChatRoomId(undefined)
    const [firstRecipient] = watchRecipients ?? []
    const societyId = firstRecipient && groupChatSocietyFor(firstRecipient)
    societyId && setValue('society', societyId)
  }

  const shouldRenderChangeToGroupChatButton =
    watchRecipients &&
    watchRecipients.length <= 1 &&
    !watchIsGroupchat &&
    !noGroupchatSocieties

  const shouldRenderGroupChatHeader =
    (watchRecipients &&
      watchRecipients.length > 1 &&
      !watchIsGroupchat &&
      !noGroupchatSocieties) ||
    watchIsGroupchat

  return (
    <div className="my-4 mx-6 flex flex-col gap-2">
      <div className="flex w-full">
        <div className="mt-[6px] flex">
          {translate('chatRoomsSidebar.createNewMessageForm.labels.sendTo')}
        </div>
        <div className="flex flex-1">
          <Controller
            control={control}
            name="recipients"
            render={({ field: { value } }) => (
              <AsyncSelectDropdown<RecipientOption, true>
                key={`${recipientsQuery.type}-${
                  recipientsQuery.societyId ?? 'all'
                }`}
                className="h-fit min-h-[40px] w-full"
                loadOptions={loadOptions}
                defaultOptions
                cacheOptions={`${recipientsQuery.type}-${
                  recipientsQuery.societyId ?? 'all'
                }`}
                filterOption={null}
                isOptionDisabled={(option) => !!option.isHint}
                value={(value ?? []).map(toRecipientOption)}
                onChange={(options: MultiValue<RecipientOption>) =>
                  onCustomChange(
                    options.flatMap((option) =>
                      option.recipient ? [option.recipient] : []
                    )
                  )
                }
                formatOptionLabel={formatRecipientOptionLabel}
                isMulti
                borderless
                menuListHeight={420}
                menuListWidth={325}
                showDropdownIndicator={false}
                minHeight="fit-content"
                placeholder={translate(
                  'chatRoomsSidebar.createNewMessageForm.search.placeholder'
                )}
              />
            )}
          />
        </div>
      </div>
      {shouldRenderChangeToGroupChatButton && (
        <Button
          label={translate('startNewChat.changeToGroupChat')}
          icon={IconChoices.CHAT}
          iconSize={22}
          onClick={handleChangeToGroupChat}
        />
      )}
      {shouldRenderGroupChatHeader && (
        <>
          <div className="flex items-center">
            <div>{translate('startNewChat.groupChatInputLabel')}</div>
            <div className="w-60">
              <Controller
                control={control}
                name="groupChatTitle"
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextInput
                    className="pl-2"
                    borderless
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex w-fit">
              <Controller
                control={control}
                name="society"
                render={({ field: { value, onChange } }) => (
                  <DropdownSelect
                    value={value}
                    onChange={(societyId) =>
                      onSocietyDropdownChange(societyId, onChange)
                    }
                    options={societyDropdownOptions || []}
                    dropdownWidth="w-60"
                    renderItemContent={(option) => (
                      <DropdownItemContent option={option} />
                    )}
                  />
                )}
              />
            </div>
            <div className="flex w-fit">
              <Controller
                control={control}
                name="isPrivate"
                render={({ field: { value, onChange } }) => (
                  <DropdownSelect
                    value={value}
                    onChange={onChange}
                    options={privateDropdownOptions}
                    dropdownWidth="w-60"
                    renderItemContent={(option) => (
                      <DropdownItemContent option={option} />
                    )}
                  />
                )}
              />
            </div>
          </div>
        </>
      )}
      {userIsBlockedInSociety && (
        <BlockedCallout selectedSocietyId={watchSociety} />
      )}
    </div>
  )
}
